/**
 * Datenzugriff hinter einer Schnittstelle.
 *
 * Solange kein Server existiert, läuft alles lokal im Browser (localStorage).
 * Sobald osm.kgva.ch eine API hat, genügt VITE_API_BASE in .env – die UI
 * bleibt unverändert, weil sie nur gegen dieses Interface arbeitet:
 *
 *   auth:      getSession, register, login, logout
 *   favorites: list, add, remove, update
 *   prefs:     load, save
 */

const API_BASE = import.meta.env?.VITE_API_BASE || '';
const LS_PROFILE = 'kgva.profile';
const LS_FAVORITES = 'kgva.favorites';
const LS_PREFS = 'kgva.prefs';

function read(key, fallback) {
  try {
    const raw = localStorage.getItem(key);
    return raw ? JSON.parse(raw) : fallback;
  } catch {
    return fallback;
  }
}

function write(key, value) {
  try {
    localStorage.setItem(key, JSON.stringify(value));
  } catch {
    /* Privater Modus / voller Speicher: Daten bleiben nur in dieser Sitzung */
  }
}

const scope = (profile) => (profile ? `${LS_FAVORITES}.${profile.id}` : `${LS_FAVORITES}.guest`);

/* ---------------------------------------------------------------- lokal --- */

const localBackend = {
  mode: 'local',

  auth: {
    async getSession() {
      return read(LS_PROFILE, null);
    },
    async register(name) {
      return this.login(name);
    },
    async login(name) {
      const clean = String(name || '').trim();
      if (!clean) throw new Error('Bitte einen Profilnamen angeben.');
      const profile = { id: slug(clean), name: clean, local: true };
      write(LS_PROFILE, profile);
      return profile;
    },
    async logout() {
      try {
        localStorage.removeItem(LS_PROFILE);
      } catch { /* ignorieren */ }
    }
  },

  favorites: {
    async list(profile) {
      return read(scope(profile), []);
    },
    async add(profile, fav) {
      const items = read(scope(profile), []);
      const entry = { id: crypto.randomUUID(), createdAt: new Date().toISOString(), ...fav };
      items.unshift(entry);
      write(scope(profile), items);
      return entry;
    },
    async update(profile, id, patch) {
      const items = read(scope(profile), []).map((f) => (f.id === id ? { ...f, ...patch } : f));
      write(scope(profile), items);
      return items.find((f) => f.id === id);
    },
    async remove(profile, id) {
      write(scope(profile), read(scope(profile), []).filter((f) => f.id !== id));
    }
  },

  prefs: {
    async load(profile) {
      return read(`${LS_PREFS}.${profile ? profile.id : 'guest'}`, null);
    },
    async save(profile, prefs) {
      write(`${LS_PREFS}.${profile ? profile.id : 'guest'}`, prefs);
    }
  }
};

/* ----------------------------------------------------------------- HTTP --- */

async function api(path, options = {}) {
  const res = await fetch(`${API_BASE}${path}`, {
    credentials: 'include',
    headers: { 'Content-Type': 'application/json', ...(options.headers || {}) },
    ...options
  });
  if (res.status === 401) return null;
  if (!res.ok) throw new Error((await res.text()) || `Fehler ${res.status}`);
  return res.status === 204 ? null : res.json();
}

const httpBackend = {
  mode: 'server',

  auth: {
    getSession: () => api('/api/auth/session'),
    register: (email, password, name) =>
      api('/api/auth/register', { method: 'POST', body: JSON.stringify({ email, password, name }) }),
    login: (email, password) =>
      api('/api/auth/login', { method: 'POST', body: JSON.stringify({ email, password }) }),
    logout: () => api('/api/auth/logout', { method: 'POST' })
  },

  favorites: {
    list: () => api('/api/favorites').then((r) => r || []),
    add: (_profile, fav) => api('/api/favorites', { method: 'POST', body: JSON.stringify(fav) }),
    update: (_profile, id, patch) =>
      api(`/api/favorites/${id}`, { method: 'PATCH', body: JSON.stringify(patch) }),
    remove: (_profile, id) => api(`/api/favorites/${id}`, { method: 'DELETE' })
  },

  prefs: {
    load: () => api('/api/prefs'),
    save: (_profile, prefs) => api('/api/prefs', { method: 'PUT', body: JSON.stringify(prefs) })
  }
};

function slug(s) {
  return s.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '') || 'profil';
}

export const backend = API_BASE ? httpBackend : localBackend;
export const isLocalMode = backend.mode === 'local';
